import { useEffect, useState, useCallback, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calculator, AlertCircle, Rocket } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Import extracted sections
import { BasicInfoSection } from "./QuickInput/sections/BasicInfo";
import { ExistingAreasSection } from "./QuickInput/sections/ExistingAreas";
import { RoadWideningSection } from "./QuickInput/sections/RoadWidening";
import { RRRatesSection } from "./QuickInput/sections/RRRates";
import { SocietyExpensesSection } from "./QuickInput/sections/SocietyExpenses";
import { JVRatioSection } from "./QuickInput/sections/JVRatio";
import { FSIParametersSection } from "./QuickInput/sections/FSIParameters";
import { RevenueSaleRateSection } from "./QuickInput/sections/RevenueSaleRate";
import { InterestFinanceSection } from "./QuickInput/sections/InterestFinance";

// Import utilities
import { REQ, INVALID, isEmptyOrZero, isExplicitZero, SOCIETY_CHARGE_FIELDS } from "./QuickInput/utils";

export const QuickInput = ({ 
  formData, 
  setFormData, 
  onCalculate, 
  onQuickJump,
  plData, 
  fsiBreakdown, 
  onValidationChange, 
  constructionCosts = { "24": 2800, "36": 3000, "45": 3200, "70": 3600, "100": 4200 } 
}) => {
  
  // State for tracking confirmed zero values for society charges
  const [confirmedZeroFields, setConfirmedZeroFields] = useState({});
  const [zeroConfirmDialog, setZeroConfirmDialog] = useState({ open: false, field: null, label: null });

  // Quick Jump destination — remembered across sessions
  const [quickJumpTarget, setQuickJumpTarget] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("quickJumpTarget") || "plsheet";
    }
    return "plsheet";
  });

  const handleQuickJumpClick = useCallback(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("quickJumpTarget", quickJumpTarget);
    }
    if (onQuickJump) {
      onQuickJump(quickJumpTarget);
    }
  }, [onQuickJump, quickJumpTarget]);
  
  // Handle society charge field change with zero confirmation
  const handleSocietyChargeChange = useCallback((e) => {
    const { name, value } = e.target;
    
    // If the value is being set to 0 and not already confirmed
    if (isExplicitZero(value) && !confirmedZeroFields[name]) {
      setZeroConfirmDialog({
        open: true,
        field: name,
        label: SOCIETY_CHARGE_FIELDS[name] || name
      });
    } else {
      // If value is not zero, remove from confirmed list
      if (!isExplicitZero(value) && confirmedZeroFields[name]) {
        setConfirmedZeroFields(prev => {
          const newState = { ...prev };
          delete newState[name];
          return newState;
        });
      }
    }
    
    // Always update the form data
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }, [confirmedZeroFields, setFormData]);
  
  // Confirm zero value
  const handleConfirmZero = useCallback(() => {
    if (zeroConfirmDialog.field) {
      setConfirmedZeroFields(prev => ({
        ...prev,
        [zeroConfirmDialog.field]: true
      }));
    }
    setZeroConfirmDialog({ open: false, field: null, label: null });
  }, [zeroConfirmDialog.field]);
  
  // Cancel zero value - reset field to empty
  const handleCancelZero = useCallback(() => {
    if (zeroConfirmDialog.field) {
      setFormData(prev => ({
        ...prev,
        [zeroConfirmDialog.field]: ""
      }));
    }
    setZeroConfirmDialog({ open: false, field: null, label: null });
  }, [zeroConfirmDialog.field, setFormData]);
  
  // Validation rules for mandatory fields
  const validationErrors = useMemo(() => {
    const errors = [];
    const isGreenfield = formData.projectClassification === "greenfield" || formData.projectMode === "greenfield";
    
    // Plot Area
    if (isEmptyOrZero(formData.plotAreaPerCard)) {
      errors.push({ field: "plotAreaPerCard", label: "Plot Area" });
    }
    
    // Road Width
    if (isEmptyOrZero(formData.roadWidth)) {
      errors.push({ field: "roadWidth", label: "Road Width" });
    }
    
    // RR Rate - Plot
    if (isEmptyOrZero(formData.rrRatePlot)) {
      errors.push({ field: "rrRatePlot", label: "RR Rate - Plot" });
    }
    
    // RR Rate - Flat (only required for non-Commercial projects)
    if (formData.projectType !== "commercial" && isEmptyOrZero(formData.rrRateFlat)) {
      errors.push({ field: "rrRateFlat", label: "RR Rate - Flat" });
    }
    
    // RR Rate - Office/Shop (required for Commercial projects)
    if (formData.projectType === "commercial") {
      if (isEmptyOrZero(formData.rrRateOffice)) {
        errors.push({ field: "rrRateOffice", label: "RR Rate - Office" });
      }
      if (isEmptyOrZero(formData.rrRateShop)) {
        errors.push({ field: "rrRateShop", label: "RR Rate - Shop" });
      }
    }
    
    // Check for narrow road (< 9m) - TDR and Paid FSI not applicable
    const roadWidthVal = parseFloat(formData.roadWidth) || 0;
    const roadWidthMeters = formData.roadWidthUnit === "feet" ? roadWidthVal * 0.3048 : roadWidthVal;
    const isNarrowRoad = roadWidthMeters > 0 && roadWidthMeters < 9;
    
    // Amenity TDR Rate (not for MHADA, not for narrow roads < 9m)
    if (formData.projectClassification !== "mhada" && !isNarrowRoad && isEmptyOrZero(formData.amenityTDRPercent)) {
      errors.push({ field: "amenityTDRPercent", label: "Amenity TDR Rate (%)" });
    }
    
    // Slum TDR Rate (not for MHADA, not for narrow roads < 9m)
    if (formData.projectClassification !== "mhada" && !isNarrowRoad) {
      if (formData.slumTDRMethod === "zone_drc") {
        if (isEmptyOrZero(formData.slumTDRZoneDRC)) {
          errors.push({ field: "slumTDRZoneDRC", label: "Slum TDR Zone DRC (₹/sft)" });
        }
      } else {
        if (isEmptyOrZero(formData.slumTDRRRPercent)) {
          errors.push({ field: "slumTDRRRPercent", label: "Slum TDR (% of RR)" });
        }
      }
    }
    
    // Society costs (only for non-greenfield/redevelopment)
    if (!isGreenfield) {
      // Rent - check based on rent type (allow confirmed zeros)
      if (formData.rentType === "per_sft" || !formData.rentType) {
        if (isEmptyOrZero(formData.rentPerSqftFlat) && !confirmedZeroFields.rentPerSqftFlat) {
          errors.push({ field: "rentPerSqftFlat", label: "Rent - Flat (₹/sft)" });
        }
      }
      // Per Number rent validation - check flat counts match members
      if (formData.rentType === "per_number") {
        const total = (parseInt(formData.count1BHK) || 0) + 
                      (parseInt(formData.count2BHK) || 0) + 
                      (parseInt(formData.count3BHK) || 0) + 
                      (parseInt(formData.count4BHK) || 0);
        const members = parseInt(formData.numMembers) || 0;
        if (total > 0 && total !== members) {
          errors.push({ field: "flatCounts", label: `Flat counts (${total}) must equal Number of Members (${members})` });
        }
      }
      // Shifting (allow confirmed zeros)
      if (isEmptyOrZero(formData.shiftingCharges) && !confirmedZeroFields.shiftingCharges) {
        errors.push({ field: "shiftingCharges", label: "Shifting Charges" });
      }
      // Corpus (allow confirmed zeros)
      if (isEmptyOrZero(formData.corpusCharges) && !confirmedZeroFields.corpusCharges) {
        errors.push({ field: "corpusCharges", label: "Corpus Charges" });
      }
      // Betterment (allow confirmed zeros)
      if (isEmptyOrZero(formData.bettermentCharges) && !confirmedZeroFields.bettermentCharges) {
        errors.push({ field: "bettermentCharges", label: "Betterment Charges" });
      }
    }
    
    // Sale Rate - Flats (required for non-Commercial)
    if (formData.projectType !== "commercial" && isEmptyOrZero(formData.saleRateFlats || formData.saleRatePerSqft)) {
      errors.push({ field: "saleRateFlats", label: "Sale Rate - Flats (₹/sft)" });
    }
    
    // Sale Rate - Office/Shop (required for Commercial)
    if (formData.projectType === "commercial") {
      if (isEmptyOrZero(formData.saleRateOffices)) {
        errors.push({ field: "saleRateOffices", label: "Sale Rate - Offices (₹/sft)" });
      }
      if (isEmptyOrZero(formData.saleRateShops)) {
        errors.push({ field: "saleRateShops", label: "Sale Rate - Shops (₹/sft)" });
      }
    }
    
    // Construction Cost
    if (isEmptyOrZero(formData.constructionCostPerSqft)) {
      errors.push({ field: "constructionCostPerSqft", label: "Construction Cost (₹/sft)" });
    }
    
    return errors;
  }, [formData, confirmedZeroFields]);
  
  const hasErrors = validationErrors.length > 0;
  
  // Helper to check if a specific field has error
  const hasFieldError = useCallback((fieldName) => {
    return validationErrors.some(e => e.field === fieldName);
  }, [validationErrors]);
  
  // Get input class based on validation
  const getInputClass = useCallback((fieldName, baseClass = "rounded-sm") => {
    return hasFieldError(fieldName) ? INVALID : baseClass;
  }, [hasFieldError]);

  // Notify parent component of validation state changes
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(hasErrors);
    }
  }, [hasErrors, onValidationChange]);

  // Form handlers
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    if (name === "projectName") {
      setFormData(prev => ({
        ...prev,
        projectName: value,
        societyName: value
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  }, [setFormData]);

  const handleSelectChange = useCallback((name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }, [setFormData]);

  const handleCheckboxChange = useCallback((name, checked) => {
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  }, [setFormData]);

  // Shared props for all sections
  const sectionProps = {
    formData,
    setFormData,
    handleChange,
    handleSelectChange,
    handleCheckboxChange,
    handleSocietyChargeChange,
    getInputClass,
    hasFieldError,
    confirmedZeroFields,
    fsiBreakdown,
    plData,
    constructionCosts
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="rounded-sm shadow-sm bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
        <CardHeader>
          <CardTitle className="font-heading tracking-tight text-2xl flex items-center gap-2">
            <Calculator className="h-6 w-6 text-primary" />
            Quick Input Sheet
          </CardTitle>
          <CardDescription>
            Enter all project details in one place. Fill in the fields below and click Calculate to see feasibility results.
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Sticky Quick Jump bar - shown only after first calculation has produced results */}
      {plData && plData.totalExpenses > 0 && onQuickJump && (
        <div
          className="sticky top-2 z-30 rounded-sm border border-primary/40 bg-white/95 backdrop-blur-sm shadow-md px-3 py-2 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3"
          data-testid="quick-jump-bar"
        >
          <div className="flex items-center gap-2 text-sm font-semibold text-primary">
            <Rocket className="h-4 w-4" />
            <span>Quick Jump</span>
          </div>
          <span className="text-xs text-muted-foreground hidden sm:inline">
            Re-run with current changes and jump straight to:
          </span>
          <div className="flex items-center gap-2 sm:ml-auto w-full sm:w-auto">
            <Select value={quickJumpTarget} onValueChange={setQuickJumpTarget}>
              <SelectTrigger className="rounded-sm h-9 w-full sm:w-[180px]" data-testid="quick-jump-target-select">
                <SelectValue placeholder="Select destination" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="fsi" data-testid="qj-fsi">FSI Calc</SelectItem>
                <SelectItem value="costs" data-testid="qj-costs">FSI Costs</SelectItem>
                <SelectItem value="society" data-testid="qj-society">Society</SelectItem>
                <SelectItem value="dacharges" data-testid="qj-dacharges">DA Charges</SelectItem>
                <SelectItem value="othercosts" data-testid="qj-othercosts">Other Costs</SelectItem>
                <SelectItem value="revenue" data-testid="qj-revenue">Revenue</SelectItem>
                <SelectItem value="plsheet" data-testid="qj-plsheet">P&amp;L Sheet</SelectItem>
                <SelectItem value="sensitivity" data-testid="qj-sensitivity">Sensitivity</SelectItem>
                <SelectItem value="dashboard" data-testid="qj-dashboard">Dashboard</SelectItem>
                <SelectItem value="comparison" data-testid="qj-comparison">Comparison</SelectItem>
                <SelectItem value="analysis" data-testid="qj-analysis">Analysis</SelectItem>
                <SelectItem value="cashflow" data-testid="qj-cashflow">Cash Flow</SelectItem>
              </SelectContent>
            </Select>
            <Button
              type="button"
              size="sm"
              onClick={handleQuickJumpClick}
              disabled={hasErrors}
              className="rounded-sm gap-2 whitespace-nowrap"
              data-testid="quick-jump-btn"
            >
              <Rocket className="h-4 w-4" />
              Run &amp; Go
            </Button>
          </div>
        </div>
      )}

      {/* Validation Errors Alert */}
      {hasErrors && (
        <Alert variant="destructive" className="rounded-sm" data-testid="validation-alert">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <span className="font-medium">Please fill required fields:</span>
            <span className="ml-2">{validationErrors.map(e => e.label).join(", ")}</span>
          </AlertDescription>
        </Alert>
      )}

      {/* Quick Stats - shown when plData is available */}
      {plData && plData.totalExpenses > 0 && (
        <Card className="rounded-sm shadow-sm border-blue-200 bg-blue-50/30" data-testid="quick-stats-card">
          <CardContent className="py-3">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-9 gap-4 text-center">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Total Constructed Area</p>
                <p className="font-mono font-bold text-lg" data-testid="total-constructed-area">
                  {fsiBreakdown?.totalFSIWithAncillary 
                    ? `${Math.round(fsiBreakdown.totalFSIWithAncillary).toLocaleString('en-IN')} sqm`
                    : '—'}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Area for Sale</p>
                <p className="font-mono font-bold text-lg" data-testid="total-saleable-area">
                  {plData.finalSaleAreaSqft > 0 
                    ? `${Math.round(plData.finalSaleAreaSqft).toLocaleString('en-IN')} sft`
                    : '—'}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Est. Flats for Sale</p>
                <p className="font-mono font-bold text-lg" data-testid="total-units">
                  {plData.finalSaleAreaSqft > 0 
                    ? Math.ceil(plData.finalSaleAreaSqft / 1500).toLocaleString('en-IN')
                    : '—'}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Cost / Sft (Saleable)</p>
                <p className="font-mono font-bold text-lg" data-testid="cost-per-sft">
                  ₹{plData.finalSaleAreaSqft > 0 ? Math.round(plData.totalExpenses / plData.finalSaleAreaSqft).toLocaleString('en-IN') : '—'}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Sharing Ratio</p>
                <p className="font-mono font-bold text-lg" data-testid="sharing-ratio">
                  {plData.societySharePercent > 0
                    ? `${plData.societySharePercent.toFixed(0)}:${plData.developerSharePercent.toFixed(0)}`
                    : '—'}
                </p>
                <p className="text-[10px] text-muted-foreground">{formData.projectMode === "greenfield" ? "Landowner : Developer" : "Society : Builder"}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Profit / Sft</p>
                <p className="font-mono font-bold text-lg" data-testid="profit-per-sft">
                  ₹{plData.finalSaleAreaSqft > 0 ? Math.round(plData.grossProfit / plData.finalSaleAreaSqft).toLocaleString('en-IN') : '—'}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Profit Margin</p>
                <p className={`font-mono font-bold text-lg ${plData.grossProfit >= 0 ? 'text-green-700' : 'text-destructive'}`} data-testid="profit-margin">
                  {plData.totalRevenue > 0 ? `${(plData.grossProfit / plData.totalRevenue * 100).toFixed(1)}%` : '—'}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Init. Invest — Full</p>
                <p className="font-mono font-bold text-lg" data-testid="qs-initial-investment-full">
                  {plData.initialInvestment > 0
                    ? `₹${(plData.initialInvestment / 1e7).toFixed(2)} Cr`
                    : '—'}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Init. Invest — Basic</p>
                <p className="font-mono font-bold text-lg text-blue-700" data-testid="qs-initial-investment-basic">
                  {plData.initialInvestmentBasicMode != null && plData.initialInvestmentBasicMode > 0
                    ? `₹${(plData.initialInvestmentBasicMode / 1e7).toFixed(2)} Cr`
                    : '—'}
                </p>
                <p className="text-[10px] text-muted-foreground">Basic FSI 1.1 + anc.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Section 1: Basic Project Info */}
      <BasicInfoSection {...sectionProps} />

      {/* Section 2: Existing Areas & Property Details (Redevelopment only) */}
      <ExistingAreasSection {...sectionProps} />

      {/* Section 2b: Road Widening */}
      <RoadWideningSection {...sectionProps} />

      {/* Section 3: Ready Reckoner Rates */}
      <RRRatesSection {...sectionProps} />

      {/* Section 4: Society Expenses (Redevelopment only) */}
      <SocietyExpensesSection {...sectionProps} />

      {/* Section 4b: JV Ratio (Greenfield only) */}
      <JVRatioSection {...sectionProps} />

      {/* Section 5: FSI & Construction Parameters */}
      <FSIParametersSection {...sectionProps} />

      {/* Section 6: Sale Rates & Revenue */}
      <RevenueSaleRateSection {...sectionProps} />

      {/* Section 7: Interest & Finance */}
      <InterestFinanceSection {...sectionProps} />

      {/* Calculate Button */}
      <Card className="rounded-sm shadow-sm border-primary/30 bg-primary/5" data-testid="quick-calculate">
        <CardContent className="py-6">
          <div className="flex flex-col items-center gap-4">
            <p className="text-center text-muted-foreground">
              Once you've filled in the details above, click the button to calculate FSI and view feasibility analysis.
            </p>
            <Button
              type="button"
              size="lg"
              onClick={onCalculate}
              className="px-8 py-6 text-lg font-semibold gap-2"
              data-testid="quick-calculate-btn"
            >
              <Calculator className="h-5 w-5" />
              Calculate FSI & View Results
            </Button>
            <p className="text-xs text-muted-foreground">
              This will take you to FSI Calculation. Then navigate to P&L Sheet for complete analysis.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Zero Value Confirmation Dialog */}
      <AlertDialog open={zeroConfirmDialog.open} onOpenChange={(open) => !open && handleCancelZero()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Zero Value</AlertDialogTitle>
            <AlertDialogDescription>
              You have entered <strong>₹0</strong> for <strong>{zeroConfirmDialog.label}</strong>.
              <br /><br />
              Are you sure you want to proceed with zero value for this society charge?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelZero}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmZero}>Yes, Confirm ₹0</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default QuickInput;
